import React, { useCallback } from 'react';
import css from './CyclingTitle.module.css';

/**
 * CyclingTitle
 *
 * Props:
 *   pageLabel        {string}   The primary page name, e.g. "track"
 *   filters          {string[]} Ordered cycle values, filters[0] is the default
 *   labels           {string[]} Optional display labels matching filters order.
 *                               If omitted, filter values are displayed directly.
 *                               Use to show "habits" while value stays "routines".
 *   value            {string}   Current active filter (controlled)
 *   onChange         {fn}       Called with the next filter value on each tap
 *   alwaysShowSuffix {boolean}  Default false. When true, filters[0] also shows
 *                               the middot + suffix (useful when the "default"
 *                               state still needs a visible label, e.g. "calendar · today")
 *   ariaLabel        {string}   Optional override for the aria-label
 */
export default function CyclingTitle({
  pageLabel,
  filters,
  labels,
  value,
  onChange,
  alwaysShowSuffix = false,
  ariaLabel,
}) {
  const cycle = useCallback(() => {
    const i = filters.indexOf(value);
    onChange(filters[(i + 1) % filters.length]);
  }, [filters, value, onChange]);

  const onKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      cycle();
    }
  }, [cycle]);

  const isDefault = value === filters[0];
  // Use labels[i] if provided, otherwise fall back to filter value
  const idx = filters.indexOf(value);
  const displayValue = labels && labels[idx] != null ? labels[idx] : value;
  // When alwaysShowSuffix, show suffix even for filters[0]
  const suffix = (alwaysShowSuffix || !isDefault) ? displayValue : null;

  const label = ariaLabel || `current view: ${displayValue}, tap to cycle`;

  return (
    <div
      className={css.cyclingTitle}
      onClick={cycle}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-label={label}
    >
      <span className={css.word}>{pageLabel}</span>
      {suffix && (
        <>
          <span className={css.dot}>·</span>
          <span className={css.suffix}>{suffix}</span>
        </>
      )}
    </div>
  );
}
